import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from '../utils/axios';
import GoogleSignInButton from '../components/GoogleSignInButton';

// Import the waiver HTML as a string
import waiverHtml from '../gym_app_liability_waiver';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);

  useEffect(() => {
    // Check if Google OAuth is configured
    const checkGoogleOAuthStatus = async () => {
      try {
        const response = await axios.get('/api/auth/google-status');
        setGoogleOAuthEnabled(response.data.googleOAuthEnabled);
      } catch (error) {
        console.error('Failed to check Google OAuth status:', error);
        setGoogleOAuthEnabled(false);
      }
    };

    checkGoogleOAuthStatus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreed) {
      setError('You must agree to the liability waiver to register.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      const response = await axios.post('/api/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.requiresEmailVerification) {
        // Show success message and redirect to verification page
        navigate('/verify-email', { 
          state: { 
            email: formData.email,
            message: response.data.message 
          } 
        });
      } else if (response.data.message && response.data.message.includes('User created successfully')) {
        navigate('/login');
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Failed to register. Please try again.');
    }
  };

  const handleGoogleSignIn = () => {
    // Redirect to Google OAuth
    // Use relative URL since frontend and backend are on same domain
    const apiUrl = process.env.REACT_APP_API_URL;
    const baseUrl = (!apiUrl || apiUrl === '' || apiUrl === '""' || apiUrl.trim() === '') 
      ? window.location.origin 
      : apiUrl;
    window.location.href = `${baseUrl}/auth/google`;
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Create an Account
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  name="agreed"
                  color="primary"
                  required
                />
              }
              label={<>
                I have read and agree to the{' '}
                <Button variant="text" size="small" onClick={() => setWaiverOpen(true)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                  Liability Waiver
                </Button>
              </>}
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={!agreed}
            >
              Register
            </Button>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                or
              </Typography>
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                disabled={!agreed || !googleOAuthEnabled}
                fullWidth
              />
              {!googleOAuthEnabled && (
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', display: 'block' }}>
                  Google sign-in requires setup. See GOOGLE_OAUTH_SETUP.md for instructions.
                </Typography>
              )}
            </Box>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Login here
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Dialog open={waiverOpen} onClose={() => setWaiverOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Liability Waiver and Release Agreement</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 500, overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: waiverHtml }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiverOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Register; 